import toast from "react-hot-toast";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { ATTACHMENT_TYPES, PCCL_STORAGE_DOCUMENTS } from "../../../Common/Constants";
import { getParsedLocalStorage } from "../../../Utils/storage-utils";
import { extractUserDataFromPermission } from "../../../Utils/Utils";
import { userIdSelector } from "../../../features/authentication/auth-slice";
import document_management_apis, { EditDocumentPayload } from "../../../Services/document-management-apis";
import {
  DocumentFieldType,
  DocumentFormFieldType,
  DocumentManagementFormType,
  SingleDocument,
} from "../../../types/document-management/document-management-types";
import { Customer } from "../../../types";
import DocumentManagementForm from "../../Document-Management/form/DocumentManagementForm";

interface EditDocumentModalProps {
  handleClose: () => void;
  defaultValues: SingleDocument;
  customer?: Customer;
}
const EditDocumentModal: React.FC<EditDocumentModalProps> = ({ handleClose, defaultValues, customer }) => {
  const [editDocument] = document_management_apis.useEditDocumentMutation();
  const user_id = useSelector(userIdSelector);

  const user_permissions = useMemo(() => {
    const permissions = getParsedLocalStorage("permissions");
    const _permissions = extractUserDataFromPermission(permissions);
    const {
      innerKeysList: sectionList,
      customerList,
      innerToCustomerNames,
      innerToCustomers,
      outerToInnerKeys,
    } = _permissions;
    return {
      sectionList: sectionList as string[],
      customerList: customerList as Customer[],
      sectionToCustomerNameMap: innerToCustomerNames as Record<string, string[]>,
      sectionToCustomersMap: innerToCustomers as Record<string, Customer[]>,
      divisionToSectionMap: outerToInnerKeys as Record<string, string[]>,
    };
  }, []);

  // const selected_section_and_customer = useMemo(() => {
  //   const selected_customer = user_permissions.customerList.find((customer) => customer.customerId === customer_id);
  //   if (!selected_customer) return { selected_customer: null, selected_section: null };

  //   for (const customer in user_permissions.sectionToCustomerNameMap) {
  //     const customer_list = user_permissions.sectionToCustomerNameMap[customer];

  //     if (customer_list.includes(selected_customer?.customerName)) {
  //       return { selected_customer, selected_section: customer };
  //     }
  //   }

  //   return { selected_customer: null, selected_section: null };
  // }, [user_permissions, customer_id]);

  async function handleFormValueChange(form_values: DocumentManagementFormType & { id: string; section: string[] }) {
    const { id, document_name, document_type, remarks, section } = form_values;
    const params: EditDocumentPayload = {
      body: {
        document_name,
        document_type,
        remark: remarks,
        customer_ids: getStations(section).map((stn) => stn.customerId),
        user_id,
        issued_by: null,
        published_dt: null,
      },
      id,
      user_id,
    };
    const toast_id = toast.loading("Editing...");
    try {
      await editDocument(params).unwrap();
      toast.success("Edited Document Successfully");
      handleClose();
    } catch (error) {
      // console.error("EDIT_DOCUMENT", error);
      toast.error("Error Editing Document");
    } finally {
      toast.dismiss(toast_id);
    }
  }

  const getStations = (sections: string[]) => {
    if (!sections) return [];
    if (!Array.isArray(sections)) {
      return [];
    }
    const allOptions: any[] = [];
    sections?.forEach((section) => {
      const options = user_permissions.sectionToCustomersMap[section];
      if (options) allOptions.push(...options);
    });
    return allOptions;
  };

  const fields: DocumentFormFieldType[] = [
    { type: DocumentFieldType.Textfield, label: "Document Name", id: "document_name", required: "Field is required" },
    {
      type: DocumentFieldType.REACTIVE_DROPDOWN,
      label: "Division",
      id: "section",
      multiple: true,
      // options: user_permissions.sectionList,
      reactive_key: "division",
      required: "Field is required",
      disabled: Boolean(customer),
      get_options: () => {
        return Object.values(user_permissions.divisionToSectionMap).flat();
      },
    },
    // {
    //   type: DocumentFieldType.REACTIVE_DROPDOWN,
    //   label: "Station",
    //   id: "station",
    //   multiple: true,
    //   // options: user_permissions.customerList,
    //   required: "Field is required",
    //   disabled: Boolean(customer),
    //   reactive_key: "section",
    //   get_options: (value: string) => {
    //     console.log("parent value is", value);
    //     const options = user_permissions.sectionToCustomersMap[value];
    //     if (!options) return [];
    //     return user_permissions.sectionToCustomersMap[value];
    //     // return ["Select All", ...user_permissions.sectionToCustomerNameMap[value]];
    //   },
    // },
    {
      type: DocumentFieldType.Dropdown,
      label: "Document Type",
      id: "document_type",
      multiple: false,
      options: PCCL_STORAGE_DOCUMENTS,
      required: "Field is required",
    },
    { type: DocumentFieldType.URL, label: "Url", id: "remarks", required: false },
  ];

  return (
    <Dialog open={true} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        Edit Document
        <CloseIcon className="assets-info-popup-close-icon pointer" onClick={handleClose} />
      </DialogTitle>
      <DialogContent>
        <DocumentManagementForm
          defaultValues={defaultValues}
          formFields={fields}
          handleFormSubmit={handleFormValueChange}
          className="upload-documents-form flex gap-4 items-center flex-wrap p-2"
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditDocumentModal;
